import { FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import React, { useCallback, useMemo } from 'react';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import NomenclaturesOverviewStore from '@/Stores/NomenclaturesOverviewStore';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import {
    DobButton,
    DobInput,
    DobLoadingIndicator,
    DobPagination,
    DobTable,
    DobTableBody,
    DobTableDataCell,
    DobTableHeader,
    DobTableHeaderCell,
    DobTableRow,
    SortState,
} from '@dedalusdiit/dob-components';
import NomenclatureOverviewModel from '@/Models/NomenclatureOverviewModel';
import Title from '@/Components/Title/Title';
import { FooterActions, FooterButtons, PageFooter } from '@/Components/PageFooter/PageFooter';
import Styles from './NomenclaturesOverview.module.scss';
import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';

interface NomenclaturesOverviewProps {
    initialState: FilingCabinetItem;
}

const COLUMN_HEADERS: { key: keyof NomenclatureOverviewModel; labelKey: string; sortable: boolean; size?: number }[] = [
    { key: 'Number', labelKey: 'Number', sortable: true },
    { key: 'ValidFrom', labelKey: 'ValidFrom', sortable: true },
    { key: 'ValidTo', labelKey: 'ValidTo', sortable: true },
    { key: 'Gender', labelKey: 'Gender', sortable: false },
    { key: 'Description', labelKey: 'Description', sortable: false, size: 200 },
    { key: 'Honorarium', labelKey: 'Honorarium', sortable: false },
    { key: 'WIGW', labelKey: 'WIGW', sortable: false },
    { key: 'Other', labelKey: 'Other', sortable: false },
    { key: 'Practice', labelKey: 'Practice', sortable: false },
    { key: 'MinEffectiveAge', labelKey: 'MinEffectiveAge', sortable: false },
    { key: 'MaxEffectiveAge', labelKey: 'MaxEffectiveAge', sortable: false },
    { key: 'MinYearAge', labelKey: 'MinYearAge', sortable: false },
    { key: 'MaxYearAge', labelKey: 'MaxYearAge', sortable: false },
    { key: 'PatientStatus', labelKey: 'A/H', sortable: false },
    { key: 'ID', labelKey: 'Delete', sortable: false },
    { key: 'LastModification', labelKey: 'LastModificationTimestamp', sortable: true },
    { key: 'LastModification_User', labelKey: 'LastModifiedBy', sortable: false },
];

const CURRENCY_COLUMNS = new Set<keyof NomenclatureOverviewModel>(['Honorarium', 'WIGW', 'Other', 'Practice']);

const NomenclaturesOverview = observer(({ initialState }: NomenclaturesOverviewProps): React.ReactElement => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const fcContext = useFilingCabinetContext();
    const store = useMemo(() => new NomenclaturesOverviewStore(new FilingCabinetStore(initialState, fcContext)), [initialState]);
    useMemo(() => store.setTitle(t('NomenclatureOverview')), []);

    const tryDelete = (e: React.MouseEvent<HTMLButtonElement>, id: number): void => {
        e.stopPropagation();
        store.tryDelete(id);
    };

    const onSortingChanged = useCallback((sortState: SortState[]): void => {
        if (sortState.length === 0) {
            return;
        }
        const first = sortState[0];
        const column = first.columnKey;
        const dir = first.direction === 'desc' ? 'desc' : 'asc';
        store.setSorting(column, dir);
    }, [store]);

    const renderCellValue = (row: NomenclatureOverviewModel, key: keyof NomenclatureOverviewModel): React.ReactNode => {
        const value = row[key];
        if (key === 'ID') {
            return <DobButton color="silent" icon="x-circle-fill" onClick={(e) => tryDelete(e, value as number)} />;
        }
        if (CURRENCY_COLUMNS.has(key)) {
            return <>&euro; {value}</>;
        }
        return <>{value}</>;
    };

    return (
        <>
            <div className={Styles.nomenclaturesOverview}>
                <Title titleName={t('NomenclatureOverview')} />

                <div className={Styles.filterOptions}>
                    <DobInput
                        value={store.state.filter}
                        onValueChange={store.setFilter}
                        placeholder={`${t('FilterOn')} ${t('NomenclatureNumber')}`}
                        className={Styles.filterInput}
                        onEnter={() => store.newSearch()}
                    />
                    <DobButton onClick={store.newSearch}>{t('Search')}</DobButton>
                </div>

                <div className={Styles.tableContainer}>
                    <DobTable
                        onSortingChanged={onSortingChanged}
                        sorting={store.state.sortColumn ? [{ columnKey: store.state.sortColumn, direction: store.state.sortOrder, order: 1 }] : []}
                        clickableRows={true}
                        color="primary"
                    >
                        <DobTableHeader>
                            <DobTableRow>
                                {COLUMN_HEADERS.map((col) => (
                                    <DobTableHeaderCell
                                        key={col.key}
                                        columnKey={col.key}
                                        sortable={col.sortable}
                                        style={{ minWidth: col.size ? `${col.size}px` : 'auto' }}
                                    >
                                        {t(col.labelKey)}
                                    </DobTableHeaderCell>
                                ))}
                            </DobTableRow>
                        </DobTableHeader>
                        <DobTableBody>
                            {store.loading && (
                                <DobTableRow>
                                    <DobTableDataCell colSpan={COLUMN_HEADERS.length}>
                                        <DobLoadingIndicator />
                                    </DobTableDataCell>
                                </DobTableRow>
                            )}

                            {!store.loading && store.state.nomenclatures.length === 0 && (
                                <DobTableRow>
                                    <DobTableDataCell colSpan={COLUMN_HEADERS.length}>
                                        {t('NoResultsFound')}
                                    </DobTableDataCell>
                                </DobTableRow>
                            )}

                            {!store.loading && store.state.nomenclatures.map((row) => (
                                <tr
                                    key={row.ID}
                                    onClick={() => void navigate('/Nomenclature/EditNomenclature/' + row.ID)}
                                >
                                    {COLUMN_HEADERS.map((col) => (
                                        <DobTableDataCell key={col.key}>
                                            {renderCellValue(row, col.key)}
                                        </DobTableDataCell>
                                    ))}
                                </tr>
                            ))}
                        </DobTableBody>
                    </DobTable>
                </div>

                <div className={Styles.pagination}>
                    <DobPagination
                        currentPage={store.state.page + 1}
                        recordsPerPage={store.state.amountToShow}
                        totalRecords={store.state.totalAmount}
                        onChange={(page) => store.setPagination(page - 1)}
                    />
                </div>

                <PageFooter>
                    <FooterActions />
                    <FooterButtons>
                        <DobButton onClick={() => void navigate('/Nomenclature/AddNomenclature')}>{t('Add')}</DobButton>
                    </FooterButtons>
                </PageFooter>
            </div>

            <Dialog open={store.showDialog}>
                <DialogTitle>{t('Delete')}</DialogTitle>
                <DialogContent>
                    <div>{t('AreYouSure')}</div>
                </DialogContent>
                <DialogButtons>
                    <DobButton color="secondary" onClick={store.cancelDelete}>{t('Cancel')}</DobButton>
                    <DobButton onClick={store.delete}>{t('Ok')}</DobButton>
                </DialogButtons>
            </Dialog>

        </>
    );
});

export default NomenclaturesOverview;
